import {PopoverPosition, Tooltip} from "@blueprintjs/core";
import styled from "@emotion/styled";
import React, {HTMLAttributes, useEffect, useRef} from "react";
import {NestedJobStop} from "../../../generated/graphql";
import {AssignmentViewState} from "../../../providers/AssignmentViewReducer";
import {MappedStop} from "../AssignmentMapV2";
import {EntityVisibilityLevel} from "../MapVisibilityContext";
import MultiStopIcon from "./MultiStopIcon";
import StopInfo, {getStopSequence, StopInfoMetadata} from "./StopInfo";
import {getAttributes} from "./StopMarker";
import {TManifestStopEnhanced} from "../../common/types";

const PackagePanelStyling = styled.div<{showJobNumber: boolean}>`
  box-sizing: border-box;
  padding: 12px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-wrap: wrap;
  max-width: 610px;
  max-height: 600px;
  overflow-y: auto;
  gap: 12px;
`;

const PackagePanelMarker = ({
  stop,
  info,
  mapJobStop,
  alwaysShowJobNumber,
  assignmentViewState,
  handleAssignmentByClickingMarker,
  usePortal,
  newWindow
}: {
  stop: NestedJobStop | TManifestStopEnhanced;
  info: StopInfoMetadata;
  mapJobStop?: number[];
  alwaysShowJobNumber?: boolean;
  assignmentViewState?: AssignmentViewState;
  handleAssignmentByClickingMarker: (info: StopInfoMetadata) => void;
  usePortal?: boolean;
  newWindow?: Window;
}) => {
  const attributes = getAttributes(stop);
  const stopSequence = getStopSequence(stop);
  const isExpanded = info.visibilityLevel === EntityVisibilityLevel.ROUTE;
  return (
    <Tooltip
      content={<StopInfo stop={stop} info={info} color={alwaysShowJobNumber ? "#000000" : "#FA545E"} />}
      openOnTargetFocus={false}
      position={PopoverPosition.TOP}
      popoverClassName={"marker-popover"}
      usePortal={usePortal}
      portalContainer={newWindow?.document.body}
    >
      <MultiStopIcon
        data-testid={`multi-stop-icon-${stop.jobStopId}`}
        position={"relative"}
        clasName={"MultiStopIcon"}
        info={info}
        jobStopId={stop.jobStopId}
        jobNumber={info.jobNumber}
        {...attributes}
        mapJobStop={mapJobStop}
        counter={stopSequence}
        showPin={true}
        showCounter={true}
        jobColor={
          (assignmentViewState?.assignmentTotalJobsNumbers || []).includes(info.jobNumber) ? "red" : info.routeColor
        }
        showJobNumber={(isExpanded && info.isFirstStop) || (isExpanded && alwaysShowJobNumber)}
        handleAssignmentByClickingMarker={handleAssignmentByClickingMarker}
      />
    </Tooltip>
  );
};

const PackagePanel: React.FC<
  HTMLAttributes<HTMLDivElement> & {
    mappedStops: MappedStop[];
    mapJobStop?: number[];
    alwaysShowJobNumber?: boolean;
    assignmentViewState?: AssignmentViewState;
    handleAssignmentByClickingMarker: (info: StopInfoMetadata) => void;
    usePortal?: boolean;
    newWindow?: Window;
    setPackagePanelOpenState?: React.Dispatch<React.SetStateAction<boolean>>;
  }
> = (props) => {
  const PackageRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    //Fix the case when popover open on newWindow, click onClose popover don't work
    if (!props.newWindow) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (props.setPackagePanelOpenState && PackageRef.current && !PackageRef.current.contains(event.target as Node)) {
        props.setPackagePanelOpenState(false);
      }
    };
    props.newWindow.addEventListener("mousedown", handleClickOutside, true);
    return () => {
      props.newWindow?.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [props]);
  return (
    <PackagePanelStyling
      data-testid="package-panel"
      ref={PackageRef}
      showJobNumber={Boolean(props.alwaysShowJobNumber)}
    >
      {props.mappedStops.map((stop) => (
        <PackagePanelMarker
          key={stop.jobStopId}
          stop={stop}
          info={stop.stopInfoMetaData}
          mapJobStop={props.mapJobStop}
          alwaysShowJobNumber={props.alwaysShowJobNumber}
          assignmentViewState={props.assignmentViewState}
          handleAssignmentByClickingMarker={props.handleAssignmentByClickingMarker}
          usePortal={props.usePortal}
          newWindow={props.newWindow}
        />
      ))}
    </PackagePanelStyling>
  );
};
export default PackagePanel;
